import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  Pin as PinIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Post-login prompt to set Mitra login PIN (authenticated session; no password).
 * Skippable — does not block dashboard access.
 */
const SetLoginPinDialog = ({
  open,
  token,
  onClose,
  onSuccess,
}) => {
  const { user, setUser } = useAuth();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetForm = () => {
    setPin('');
    setConfirmPin('');
    setShowPin(false);
    setError('');
    setSuccess('');
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    const pinDigits = String(pin || '').replace(/\D/g, '');
    const confirmDigits = String(confirmPin || '').replace(/\D/g, '');
    if (pinDigits.length < 4 || pinDigits.length > 6) {
      setError('Login PIN must be 4 to 6 digits');
      return;
    }
    if (pinDigits !== confirmDigits) {
      setError('PIN and confirm PIN do not match');
      return;
    }

    setLoading(true);
    const payload = {
      login_pin: pinDigits,
      confirm_pin: confirmDigits,
    };
    const urls = [
      `${API_BASE_URL}/franchisee/admin/set-pin`,
      `${API_BASE_URL}/auth/franchisee-admin/set-pin`,
      `${API_BASE_URL}/franchisee/set-pin`,
    ];

    try {
      let response = null;
      let lastError = null;
      for (const url of urls) {
        try {
          response = await axios.post(url, payload, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          break;
        } catch (err) {
          lastError = err;
          if (err.response?.status === 404) continue;
          throw err;
        }
      }
      if (!response) throw lastError || new Error('Set PIN endpoint not found');

      if (response.data?.success) {
        setSuccess(response.data.message || 'Login PIN set successfully');
        if (user && typeof setUser === 'function') {
          const nextUser = { ...user, has_login_pin: true };
          setUser(nextUser);
          try {
            const raw = localStorage.getItem('admin_user') || sessionStorage.getItem('admin_user');
            if (raw) {
              const parsed = JSON.parse(raw);
              const merged = { ...parsed, has_login_pin: true };
              if (localStorage.getItem('admin_user')) {
                localStorage.setItem('admin_user', JSON.stringify(merged));
              }
              if (sessionStorage.getItem('admin_user')) {
                sessionStorage.setItem('admin_user', JSON.stringify(merged));
              }
            }
          } catch (_) {
            /* ignore storage sync failures */
          }
        }
        setTimeout(() => {
          resetForm();
          onSuccess?.();
          onClose?.();
        }, 900);
      } else {
        setError(response.data?.message || 'Failed to set login PIN');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to set login PIN. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>Set your login PIN</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can sign in faster with phone + PIN. Choose a 4–6 digit PIN (no
          password required while you are signed in).
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Box sx={{ pt: 0.5 }}>
          <TextField
            fullWidth
            label="New login PIN"
            type={showPin ? 'text' : 'password'}
            value={pin}
            onChange={(e) =>
              setPin(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))
            }
            margin="normal"
            helperText="4–6 digits"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PinIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPin((v) => !v)}
                    edge="end"
                    aria-label="toggle pin visibility"
                  >
                    {showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm PIN"
            type={showPin ? 'text' : 'password'}
            value={confirmPin}
            onChange={(e) =>
              setConfirmPin(
                String(e.target.value || '').replace(/\D/g, '').slice(0, 6)
              )
            }
            margin="normal"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PinIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Skip for now
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? 'Saving…' : 'Set PIN'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetLoginPinDialog;

export function needsLoginPinSetup(user) {
  if (!user || typeof user !== 'object') return false;
  // Only prompt when backend explicitly reports missing PIN (deploy-safe).
  if (!Object.prototype.hasOwnProperty.call(user, 'has_login_pin')) {
    return false;
  }
  return user.has_login_pin !== true && user.has_login_pin !== 1;
}
